// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2024-06-05 16:56:28 +0200 (Wed, 05 Jun 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 143292 $
//  Purpose   : react hooks for config system
// =============================================================================
// ---Third-party imports

// ---Framework imports
import log from 'uix/logging'
import {Sel} from 'uix/registries'
import {
  size,
  dict,
  mapItems,
  filterItems,
  isArray,
  isFunction,
  isObject,
  isString,
  get,
  sorted,
  keys,
  values,
  flatten,
  strip,
  has,
} from 'ldash'
import {
  useSelector,
  useSelectorShallow,
  useActions,
  usePersistentShallow,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useCallApi,
  usePartContext,
} from 'uix/hooks'
import {selectRequestDomainCfg} from 'uix/selectors'
import {hashObj} from 'uix/utils'

// ---Local imports
import {
  loadDomainConfigSpec,
  loadDomainConfigSections,
  loadConfigOverlay,
  saveDomainConfig,
} from './actions'

// ---Exports

export const useDconfigOverlay = dname => {
  let overlay = useSelector(root => root.config.dconfigOverlays[dname])
  let actions = useActions({loadConfigOverlay, saveDomainConfig})

  useEffect(() => {
    if (!overlay) actions.loadConfigOverlay(dname)
  }, [actions])

  return {
    overlay,
    saveDomainConfig: actions.saveDomainConfig,
    reload: () => actions.loadConfigOverlay(dname),
  }
}

export const useEditedDomainConfigValues = dname => {
  /* manage edited config values by domain
  keys are paths like 'root.domain.color'
  */
  let [editedDomainValues, setEditedDomainValues] = usePersistentShallow(
    'editedDomainConfig',
    {},
    1
  )
  let editedValues = editedDomainValues[dname] ?? {}
  let setValue = useCallback(
    (name, value) => {
      let newList = null
      if (name === null) {
        // clear
        newList = {}
      } else {
        if (value !== undefined) {
          // remove value
          newList = {...editedValues, [name]: value}
        } else {
          // update value
          newList = dict(filterItems(editedValues, n => n !== name))
        }
      }
      setEditedDomainValues({[dname]: newList})
    },
    [setEditedDomainValues]
  )
  return [editedValues, setValue]
}

const numdots = s => (s.match(/\./g) || []).length
const getPragma = comments => {
  if (comments.length > 0 && comments.slice(-1)[0].startsWith('pragma')) {
    let last_term = comments
      .slice(-1)[0]
      .split(':')
      .slice(-1)[0]
    return strip(last_term)
  }
  return null
}

const generateData = (
  section,
  names,
  levels,
  dconfigSpec,
  dconfig,
  dconfigOverlay,
  exclude,
  include
) => {
  // track offsets for ordering of __many__ sections
  let orderOffsetsMany = {}
  let orderOffset = 0

  let getSpec = (name, value) => {
    let splitted = name.split('.')
    let scalarName = splitted.slice(-1)[0]
    let isMany = splitted.slice(-2)[0] === '__many__'
    if (isMany) {
      let manyPath = splitted.slice(1, -1).join('.')
      // xxx.yyy.zzz.__many__.scalar => xxx.yyy.__many__
      splitted = splitted.slice(1, -3)
      splitted.push('__many__')
      if (!orderOffsetsMany[manyPath]) {
        orderOffsetsMany[manyPath] = orderOffset
        orderOffset += 100 // at least we wont collide (size(sec._order_) would be correct)
      }
    } else {
      splitted = splitted.slice(1, -1)
    }

    let path = splitted.join('.')

    let sec = get(dconfigSpec, path)
    if (!sec) {
      log.warn('invalid sec %o', path)
      return {}
    }

    if (isMany) {
      var overlayValue = get(dconfigOverlay, `${name.slice(5).replace('.__many__.', '.')}`)
    } else {
      var overlayValue = get(dconfigOverlay, `${path}.${scalarName}`)
    }

    let comments = sec._comments_[scalarName] || []

    if (getPragma(comments) === 'hidden') {
      return {} // items without name are filtered later
    }

    let defaultValue = sec._defaults_[scalarName]
    //~ if (name.includes('access'))
    //~ log('%o %o %o %o %O', path, scalarName, defaultValue, value, orderOffset)

    return {
      name,
      scalarName,
      comments,
      defaultValue,
      overlayValue,
      spec: sec._spec_[scalarName],
      order: sec._order_[scalarName] + orderOffset,
    }
  }

  let excl = n => false
  if (isArray(exclude) || isString(exclude)) {
    let secs = isString(exclude) ? new Set([exclude]) : new Set(exclude)
    excl = n => secs.has(n)
  } else if (isFunction(exclude)) {
    excl = exclude
  }

  let incl = new Set(include || [])
  let whiteList = n =>
    (!n.startsWith('_') || n === '__many__') && !excl(n) && (size(incl) == 0 || incl.has(n))

  let walkNames = (sec, levels, prefix) => {
    let names = keys(sec._order_)
    names = names.map(n => prefix + '.' + n)
    if (levels > 0) {
      let subSections = filterItems(sec, (n, subsec) => whiteList(n))

      let childnames = subSections.map(([n, subsec]) => {
        if (n === '__many__') {
          // fetch names for __many__ sections now from dconfig
          let sec_spec_ = sec._spec_ || {}

          let secCandidates = keys(get(dconfig, prefix.slice(5), {}))
          // filter scalars and non __many__ sections
          secCandidates = secCandidates.filter(
            n => !sec_spec_[n] && !sec[n] && !['TRACKED', 'EXPLICIT', 'DEFAULTS'].includes(n)
          )
          let manyNames = flatten(
            // will build a name like dconfig.sec1.manysec2.__many__.scalar1
            secCandidates.map(scn => walkNames(subsec, levels - 1, `${prefix}.${scn}.${n}`))
          )
          return manyNames
        } else {
          return walkNames(subsec, levels - 1, `${prefix}.${n}`)
        }
      })
      names = [...names, ...flatten(childnames)]
    }

    return names
  }

  if (section) {
    if (!names) {
      // only section given
      let sec = section == 'root' ? dconfigSpec : get(dconfigSpec, section, {})
      if (levels == 1) {
        names = keys(sec._order_).map(n => `root.${section}.${n}`)
      } else {
        names = walkNames(sec, levels, section)
        if (section !== 'root') {
          names = names.map(n => `root.${n}`)
        }
      }
      //~ log.x(section, levels, names)
      //~ log.x(dconfigSpec)
      //~ log.x(dconfig)

      // we try to filter sections here
      names = names.filter(n => !get(dconfigSpec, n))
    } else {
      // section + names given
      names = names.map(n => `root.${section}.${n}`)
    }
    //~ names.push('root.' + section)
  } else if (!names) {
    log.warn('no names and no section given')
    return
  } else {
    // only names given
    let decodeSectionWidcard = n => {
      if (n.endsWith('.*')) {
        let section = n.slice(0, -2)
        return walkNames(get(dconfigSpec, section, {}), levels, section)
      } else {
        return [n]
      }
    }
    names = flatten(names.map(n => decodeSectionWidcard(n))).map(n => `root.${n}`)
  }

  let sectionOffset = section ? numdots(section) : 0

  let data = names.map(n => {
    let parts = n.split('.')
    let isManySec = parts.slice(-2)[0] === '__many__' ? 1 : 0
    if (isManySec) {
      var value = get(dconfig, `${n.slice(5).replace('.__many__.', '.')}`, undefined)
    } else {
      var value = get(dconfig, `${n.slice(5)}`, undefined)
    }
    return {
      level: parts.length - sectionOffset - isManySec,
      label: parts.slice(-1)[0],
      value,
      ...getSpec(n, value),
    }
  })

  if (section) {
    // use section order
    data = sorted(data, d => d.order)
  } // else
  return data
}

const MAGICS = ['EXPLICIT', 'DEFAULTS', 'TRACKED']
const BROKEN = ['bibformat'] // unsupported DEFAULTS/DEFAULTS structure

const resolveIncompleteManySections = (dconfigLoaded, dconfigSpec) => {
  /* resolve __many__ section in dconfigSpec
  - we need a loaded dconfig for information about materialized sections
  - copy __many__ spec into (now known) keys and write fractional _order_ values
  */

  dconfigSpec = dict(filterItems(dconfigSpec, k => !BROKEN.includes(k)))

  let recurse = (node, path) => {
    return dict(
      mapItems(
        node,
        (k, v) => {
          if (isObject(v)) {
            let p = [...path, k]
            if (v.__many__) {
              let dotted = p.join('.')
              let loaded = get(dconfigLoaded, dotted)

              if (loaded) {
                let new_names = keys(loaded).filter(
                  sec_name =>
                    !has(v, sec_name) && !has(v._defaults_, sec_name) && !MAGICS.includes(sec_name)
                )
                let new_sections = dict(
                  new_names.map((many_sec_name, i) => {
                    // trying to push new order into fractionals
                    // XXX: this will only work for one-level __many__ section
                    let _sec_order_ = Math.max(...values(v._order_), v._sec_order_)
                    _sec_order_ = _sec_order_ + (i + 1) * (1 / (new_names.length + 1))
                    // scalar order
                    let _order_ = dict(
                      mapItems(v.__many__._order_, (k, v, i) => [k, _sec_order_ + i * 0.0001])
                    )
                    let many_copied = {...v.__many__, _sec_order_, _order_}

                    return [many_sec_name, many_copied]
                  })
                )
                v = {...v, ...new_sections}
              }
            }
            return [k, recurse(v, p)]
          } else {
            return [k, v]
          }
        }
        //~ , (k, v) => !MAGICS.includes(k)
      )
    )
  }

  return recurse(dconfigSpec, [])
}

export const useDomainConfigEditDetails = ({
  section,
  names = null,
  levels = 1,
  exclude = null,
  include = null,
}) => {
  /*  trigger loading of dconfigSpec and of missing sections
   */
  let {dname} = usePartContext()

  let {dconfigLoaded, dconfigSpec, dconfigOverlay} = useSelectorShallow(root => ({
    dconfigLoaded: root.config.dconfigEditor[dname],
    dconfigSpec: root.config.dconfigSpec,
    dconfigOverlay: root.config.dconfigOverlays[dname],
  }))

  useEffect(() => {
    if (!dconfigSpec) act.loadDomainConfigSpec()
  }, [])

  let act = useActions({
    loadDomainConfigSpec,
    loadDomainConfigSections,
    loadConfigOverlay,
  })
  // trigger loading of dconfigSpec and of missing sections
  let specAvailable = dconfigSpec && dconfigSpec !== 'LOADING'
  let overlayAvailable = dconfigOverlay && dconfigOverlay != 'LOADING'

  let dconfigSpecResolved = useMemo(
    () => (specAvailable ? resolveIncompleteManySections(dconfigLoaded, dconfigSpec) : dconfigSpec),
    [dconfigLoaded, specAvailable, dconfigSpec]
  )

  useEffect(() => {
    if (!overlayAvailable) {
      act.loadConfigOverlay(dname)
    }
  }, [dconfigOverlay])

  let dataArgs = [
    section,
    names,
    levels,
    dconfigSpecResolved,
    dconfigLoaded,
    dconfigOverlay?.root,
    exclude,
    include,
  ]

  let data = useMemo(() => {
    if (!specAvailable || !overlayAvailable) {
      return null
    }
    return generateData(...dataArgs)
  }, [...dataArgs, specAvailable, overlayAvailable])

  useEffect(() => {
    if (data) {
      let dcfgLoaded = dconfigLoaded || {}
      // trigger loading of missing sections (derived by checking list of scalars with undefined values)
      let ignore = n => ['__many__', 'TRACKED', 'EXPLICIT'].includes(n)
      // XXX: there should be no items without .name - but there are (is one, domain.type?)
      let missingNames = data
        .filter(
          d =>
            d.name && d.value === undefined && !ignore(d.scalarName) && !d.name.endsWith('__many__')
        )
        .map(d => d.name)
      let missing = new Set(missingNames.map(d => d.split('.')[1]))
      missing = [...missing.keys()]

      // if we request a __many__ section (without explicit scalar names)
      // we wont get any missingNames above (from non __many__ scalars)
      if (section && section !== 'root') {
        let majorSec = section.split('.')[0]
        if (!dcfgLoaded[majorSec] && !missing.includes(majorSec)) {
          missing.push(majorSec)
        }
      }
      // remove loading and loaded sections
      missing = missing.filter(
        majorSec => dcfgLoaded[majorSec] !== 'LOADING' && !isObject(dcfgLoaded[majorSec])
      )

      if (missing.length > 0) {
        log.debug(
          '%o missing sections %o, ... for [%o]',
          missing.length,
          missing.slice(0, 2),
          missingNames.slice(0, 2)
        )
        act.loadDomainConfigSections(dname, missing)
      }
    }
  }, [data, dconfigLoaded, section])

  return {spec: data, loading: !specAvailable}
}

export const useDomainConfigOverride = cfgdata => {
  let setDebugData = useActions('setDebugData')
  useEffect(() => {
    setDebugData({dconfigOverride: cfgdata})
  }, [hashObj(cfgdata)])
}

export const useDomainConfigSave = () => {
  let {dname} = useSelector(Sel.request)
  let {overlay, reload} = useDconfigOverlay(dname)

  let {callApi, calling: saving} = useCallApi('updateDomainConfigValues')
  // params are dotted key/value pairs from dconfig
  // like {'templating.mods.show': 'true'}
  let updateDomainConfigValues = useCallback(params => callApi(params, reload), [callApi, dname])
  return {
    overlay,
    saveValues: updateDomainConfigValues,
    saving,
    loading: overlay && !!overlay.loading,
  }
}
